import React, { useEffect, useState, Suspense } from 'react';
import { Redirect, Route, Switch, useLocation, useHistory } from 'react-router-dom';
import {
  IonApp,
  IonContent,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react';
import NotificationsIcon from '@material-ui/icons/Notifications';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { connect } from 'react-redux';
import { useInterval } from '../hooks/useInterval';
import '../assets/global_styles/marquee.scss';
import './UserRouter.scss';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader

import {
  enableCommission,
  fetchBalance,
  getHouseIdFromToken,
  getParentIdFromToken,
  setAllowedConfig,
  setDomainConfig,
} from '../store';

import { RootState } from '../models/RootState';
import { Notification } from '../models/Notification';
import { DomainConfig } from '../models/DomainConfig';

import { ALLOW_CASINO } from '../constants/CasinoPermission';
import { CONFIG_PERMISSIONS } from '../constants/ConfigPermission';
import { BRAND_DOMAIN } from '../constants/Branding';

import {
  checkPNStompClientSubscriptions,
  connectToPushNotification,
  disConnectToPushNotificationWS,
  subscribeWsForNotifications,
  subscribeWsForNotificationsPerAdmin,
} from '../webSocket/webSocket';

import SVLS_API from '../api-services/svls-api';
import { isIOS } from 'react-device-detect';
import { AxiosResponse } from 'axios';
import { showThemes } from '../util/stringUtil';

// Lazy-loaded components
const Home = React.lazy(() => import('../pages/Home/Home'));
const UserPLStatement = React.lazy(() => import('../pages/UserPLStatement/UserPLStatement'));
const AccountStatement = React.lazy(() => import('../pages/AccountStatement/AccountStatement'));
const AccountStatementEventLevelView = React.lazy(() => import('../pages/AccountStatement/AccountStatementEventLevel'));
const CasinoNew = React.lazy(() => import('../pages/Casino/CasinoNew/CasinoNew'));
const CasinoIframeNew = React.lazy(() => import('../pages/Casino/CasinoIframeNew/CasinoIframeNew'));
const Poker = React.lazy(() => import('../pages/Poker/Poker'));
const Header = React.lazy(() => import('../components/Header/Header'));
const UserRoute = React.lazy(() => import('../hoc/UserRoute'));
const MyWallet = React.lazy(() => import('../pages/MyWallet/MyWallet'));
const Deposit = React.lazy(() => import('../pages/Payment/Deposit'));
const Withdrawal = React.lazy(() => import('../pages/Payment/Withdrawal'));
const GameReport = React.lazy(() => import('../pages/GameReport/GameReport'));
const RulesAndRegulations = React.lazy(() => import('../pages/RulesAndRegulations/RulesAndRegulations'));
const ResponsibleGambling = React.lazy(() => import('../pages/ResponsibleGambling/ResponsibleGambling'));
const TermsConditions = React.lazy(() => import('../pages/TermsConditions/TermsConditions'));
const PunterSettings = React.lazy(() => import('../pages/PunterSettings/PunterSettings'));
const MyProfile = React.lazy(() => import('../components/MyProfile/MyProfile'));
const HelpView = React.lazy(() => import('../components/HelpView/HelpView'));
const ResetPassword = React.lazy(() => import('../pages/ResetPassword/ResetPassword'));
const MobileHeader = React.lazy(() => import('../components/Header/MobileHeader/MobileHeader'));
const ButtonVariables = React.lazy(() => import('../components/ButtonVariables/ButtonVariables'));
const Ledger = React.lazy(() => import('../pages/AccountStatement/AccountStatementICLevel'));
const ExchangeSportsBook = React.lazy(() => import('../pages/ExchSportsBook/ExchangeSportsBook'));
const AdminNotification = React.lazy(() => import('../components/AdminNotifications/AdminNotification'));
const DashboardView = React.lazy(() => import('../pages/DashboardView/DashboardView'));
const UserCommissionReport = React.lazy(() => import('../pages/UserCommission/UserCommissionReport'));
const TransactionRequest = React.lazy(() => import('../pages/TransactionRequest/TransactionRequest'));
const MyBets = React.lazy(() => import('../components/MyBets/MyBetsView'));
const UserCommissionBySportView = React.lazy(() => import('../pages/UserCommission/UserCommissionBySportView'));
const TVGamesView = React.lazy(() => import('../pages/Casino/CasinoNew/TVGamesView'));
const SubHeader = React.lazy(() => import('../views/SubHeader/SubHeader'));
const VirtualSports = React.lazy(() => import('../pages/VirtualSports/VirtualSports'));
const SportsProviderIframe = React.lazy(() => import('../pages/SportsProvider/SportsProviderIframe'));
const IndianCasinoPage = React.lazy(() => import('../pages/Casino/IndianCasino/indianCasinoPage'));
const ThemeChanges = React.lazy(() => import('../components/Header/ThemeChanges'));
const ThemeChangesPage = React.lazy(() => import('../components/Dashboard/ThemeChangesPage'));
const TurnOverStatement = React.lazy(() => import('../pages/TurnOverHistory/TurnOverStatement'));
const BonusStatement = React.lazy(() => import('../pages/BonusStatement/BonusStatement'));
const Promotion = React.lazy(() => import('../pages/Promotion/Promotion '));
const Sitemap = React.lazy(() => import('../pages/Sitemap/Sitemap'));
const ResponsibleGaming = React.lazy(() => import('../components/ResponsibleGaming/ResponsibleGaming'));
const FunGames = React.lazy(() => import('../pages/Casino/FunGames/FunGames'));
const WhatsappFloater = React.lazy(() => import('../components/WhatsappFloater/WhatsappFloater'));
const MlobbyIframeNew = React.lazy(() => import('../pages/Casino/MlobbyIframeNew/MlobbyIframeNew'));
const ChangePassword = React.lazy(() => import('../components/ChangePassword/ChangePassword'));
const UpLineReport = React.lazy(() => import('../pages/UserCommission/CommissionReport/UpLineReport'));
const Referall = React.lazy(() => import('../components/MyProfile/Referall'));

type StateProps = {
  role: string;
  allowedConfig: number;
  commissionEnabled: boolean;
  status: number;
  loggedIn: boolean;
  fetchBalance: () => void;
  setAllowedConfig: (allowedConfig: number) => void;
  setDomainConfig: (config: DomainConfig) => void;
  setEnableCommission: (commission: boolean) => void;
  balanceChanged: number;
  houseId: string;
  parentId: string;
  accountId: string;
  notificationUpdated: number;
  pushNotifWSConnection: boolean;
};

const UserRouter: React.FC<StateProps> = (props) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [config, setConfig] = useState<DomainConfig>({
    demoUser: false,
    signup: false,
    whatsapp: false,
    payments: false,
    bonus: false,
    userProfileUpdate: true,
  });

  const {
    role,
    allowedConfig,
    status,
    loggedIn,
    fetchBalance,
    setAllowedConfig,
    setDomainConfig,
    setEnableCommission,
    balanceChanged,
    houseId,
    parentId,
    accountId,
    notificationUpdated,
    pushNotifWSConnection,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const history = useHistory();
  const location = useLocation();
  const pathName = location.pathname;

  const [skins, setSkins] = useState<string[]>([]);
  const [showNotificationModal, setShowNotificationModal] =
    useState<boolean>(false);

  const fetchNotifications = async () => {
    const response = await SVLS_API.get('/catalog/v2/notifications/', {
      headers: {
        Authorization: sessionStorage.getItem('jwt_token'),
      },
      params: {
        type: 'ACTIVE',
      },
    });
    setNotifications(response.data);
  };

  const redirectUser = () => {
    let redirectionURL = null;
    if ((allowedConfig & 31) === 0) {
      if (
        (allowedConfig & CONFIG_PERMISSIONS.sports) !== 0 &&
        pathName.includes('casino')
      ) {
        redirectionURL = '/home';
      } else if (
        (allowedConfig & CONFIG_PERMISSIONS.casino) !== 0 &&
        pathName.includes('exchange_sports')
      )
        redirectionURL = '/home';
    } else if (
      (allowedConfig & CONFIG_PERMISSIONS.casino) === 0 &&
      pathName.includes('casino')
    ) {
      redirectionURL = '/home';
    } else if (
      (allowedConfig & CONFIG_PERMISSIONS.sports) === 0 &&
      pathName.includes('exchange_sports')
    ) {
      redirectionURL = '/home';
    } else if (
      (allowedConfig & CONFIG_PERMISSIONS.indian_casino) === 0 &&
      pathName.includes('indian_casino')
    ) {
      redirectionURL = '/home';
    } else if (
      (allowedConfig & CONFIG_PERMISSIONS.live_casino) === 0 &&
      pathName.includes('/dc/')
    ) {
      redirectionURL = '/home';
    }

    if (redirectionURL) history.push(redirectionURL);
  };

  useEffect(() => {
    getSkins();
  }, []);

  useEffect(() => {
    redirectUser();
  }, [pathName, allowedConfig, loggedIn]);

  const closeStlDialog = () => {
    setShowNotificationModal(false);
  };

  const getAllowedConfig = async () => {
    let response;
    try {
      if (!loggedIn) {
        response = await SVLS_API.get('/catalog/v2/settings/catalog-config');
      } else {
        response = await SVLS_API.get('/catalog/v2/settings/catalog-config', {
          headers: {
            Authorization: sessionStorage.getItem('jwt_token'),
          },
        });
      }
      if (response.status === 200) {
        setAllowedConfig(response.data.allowedConfig);
        setEnableCommission(response.data.commissionEnabled);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (loggedIn) {
      fetchNotifications();
    }
  }, [loggedIn, notificationUpdated]);

  useEffect(() => {
    getAllowedConfig();
    getDomainConfig();
  }, [loggedIn]);

  useEffect(() => {
    if (loggedIn) {
      fetchBalance();
    }
  }, [loggedIn, balanceChanged]);

  useEffect(() => {
    if (loggedIn) {
      let refreshInterval = setInterval(() => {
        checkPNStompClientSubscriptions();
      }, 10000);
      return () => {
        clearInterval(refreshInterval);
      };
    }
  }, []);

  const getDomainConfig = async () => {
    try {
      const response = await SVLS_API.get(`/account/v2/books/${BRAND_DOMAIN}`);
      if (response.status === 200) {
        const dConfig: DomainConfig = {
          demoUser: response.data.demo_user_enabled,
          signup: response.data.signup_enabled,
          whatsapp: response.data.whatsapp_support,
          payments: response.data.payments_enabled,
          bonus: response.data.bonus_enabled,
          userProfileUpdate: response.data.user_profile_update_enabled,
        };
        setConfig(dConfig);
        setDomainConfig(dConfig);
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Websocket handler
  useEffect(() => {
    if (loggedIn) {
      if (pushNotifWSConnection) {
        disConnectToPushNotificationWS();
      }
      connectToPushNotification();
    }

    return () => {
      if (pushNotifWSConnection) {
        disConnectToPushNotificationWS();
      }
    };
  }, [loggedIn]);

  useEffect(() => {
    if (
      loggedIn &&
      !window.location.pathname.includes('==') &&
      !window.location.pathname.includes('multi-markets')
    ) {
      if (
        loggedIn &&
        !window.location.pathname.includes('==') &&
        !window.location.pathname.includes('multi-markets')
      ) {
        subscribeWsForNotifications(false, houseId);
        subscribeWsForNotificationsPerAdmin(
          false,
          houseId,
          parentId,
          accountId
        );
      }
    }
  }, [pushNotifWSConnection, loggedIn]);

  const getNotificationMessages = (notificationsList: Notification[]) => {
    let notfMsg = ' ';
    for (let i = 0; i < notificationsList.length; i++) {
      if (notificationsList[i]) {
        notfMsg += ' ' + notificationsList[i].message;
        notfMsg += notificationsList[i + 1] ? '  ' + '  '.repeat(8) : '';
      }
    }

    return notfMsg;
  };

  const getSkins = async () => {
    try {
      const response: AxiosResponse<any> = await SVLS_API.get(
        `/account/v2/books/${BRAND_DOMAIN}`
      );
      const dConfig: DomainConfig = {
        demoUser: response.data.demo_user_enabled,
        signup: response.data.signup_enabled,
        whatsapp: response.data.whatsapp_support,
        payments: response.data.payments_enabled,
        bonus: response.data.bonus_enabled,
        userProfileUpdate: response.data.user_profile_update_enabled,
      };
      setConfig(dConfig);
      setDomainConfig(dConfig);
      setSkins(response?.data?.skins);
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <>
      {config.payments &&
      !location?.hash &&
      !location?.search &&
      location?.pathname?.charAt(location?.pathname?.length - 1) === '/' ? (
        <Redirect to={location?.pathname?.slice(0, -1)} />
      ) : null}
      {status !== -1 && status === 4 ? (
        <Redirect to="/reset-password" />
      ) : status !== -1 && status === 2 ? (
        <Redirect to="/terms-and-conditions" />
      ) : role && role !== 'User' ? (
        <Redirect to="/access-redirect" />
      ) : !ALLOW_CASINO &&
        (window.location.pathname.includes('/casino') ||
          window.location.pathname.includes('/dc/gamev1.1/')) ? (
        <Redirect to="/access-redirect" />
      ) : (
        <IonApp>
          <Header></Header>
          <SubHeader skins={skins}></SubHeader>
          {notifications?.length > 0 ? (
            <div className="marquee notifications">
              <div
                className="info web-view"
                onClick={() => setShowNotificationModal(true)}
              >
                <NotificationsIcon /> Latest
              </div>
              <div
                className="info mob-view"
                onClick={() => setShowNotificationModal(true)}
              >
                <NotificationsIcon />
              </div>
              <span>{getNotificationMessages(notifications)}</span>
            </div>
          ) : null}
          {/* </>
          ) : null} */}

          <IonContent>
            <IonRefresher
              slot="fixed"
              onIonRefresh={() => window.location.reload()}
            >
              <IonRefresherContent refreshingSpinner="circular"></IonRefresherContent>
            </IonRefresher>

            <div className="router-ctn">
              <WhatsappFloater />
              {showThemes(skins) && loggedIn && !isMobile ? (
                <ThemeChanges skins={skins} />
              ) : null}

              <Switch>
                {loggedIn && showThemes(skins) && (
                  <Route
                    path="/mo/theme"
                    component={() => <ThemeChangesPage skins={skins} />}
                    exact={true}
                  />
                )}
                <Route path="/home" component={Home} exact={true} />
                <Route path="/casino" component={CasinoNew} exact={true} />
                <Route path="/fun_games" component={FunGames} exact={true} />
                <Route path="/referall" component={Referall} exact={true} />
                <Route
                  path="/virtual_sports"
                  component={VirtualSports}
                  exact={true}
                />

                <Route path="/tv_games" component={TVGamesView} exact={true} />
                <Route
                  path="/indian_casino"
                  component={IndianCasinoPage}
                  exact={true}
                />

                <Route
                  path="/premium_sports"
                  component={SportsProviderIframe}
                  exact={true}
                />

                <Route path="/dc" component={CasinoNew} exact={true} />
                <Route path="/poker" component={Poker} exact={true} />
                <Route path="/help" component={HelpView} exact={true} />
                <Route
                  path="/resetPassword"
                  component={ResetPassword}
                  exact={true}
                />
                <Route
                  path="/change-password"
                  component={ChangePassword}
                  exact={true}
                />
                <Route
                  path="/dc/gamev1.1/:gamePath"
                  component={CasinoIframeNew}
                  exact={true}
                />

                <Route
                  path="/ic_account_statement"
                  component={Ledger}
                  exact={true}
                />

                <Route
                  path="/bonus_statement"
                  component={BonusStatement}
                  exact={true}
                ></Route>

                <Route
                  path="/bonus/turnover_history"
                  component={TurnOverStatement}
                  exact={true}
                ></Route>

                <Route
                  path="/exchange_sports"
                  render={() => <ExchangeSportsBook />}
                ></Route>
                {/* <Route path="/binary" render={() => <ExchBinaryView />}></Route> */}
                <Route
                  path="/dashboard"
                  render={() => <DashboardView />}
                ></Route>
                <UserRoute
                  path="/account_statement"
                  component={AccountStatement}
                  exact={true}
                ></UserRoute>
                <UserRoute
                  path="/market_account_statement"
                  component={AccountStatementEventLevelView}
                  exact={true}
                ></UserRoute>
                <UserRoute
                  path="/my_bets"
                  component={MyBets}
                  exact={true}
                ></UserRoute>
                {/* <UserRoute
                  path="/my_wallet"
                  component={MyWallet}
                  exact={true}
                ></UserRoute> */}

                <UserRoute
                  path="/pl_statement"
                  component={UserPLStatement}
                  exact={true}
                ></UserRoute>
                <UserRoute
                  path="/game_report"
                  component={GameReport}
                  exact={true}
                ></UserRoute>

                <UserRoute
                  path="/commission_report"
                  component={UserCommissionReport}
                  exact={true}
                ></UserRoute>
                <UserRoute
                  path="/commission_report:upline"
                  component={UpLineReport}
                  exact={true}
                ></UserRoute>

                <UserRoute
                  path="/commission_report/:eventId"
                  component={UserCommissionBySportView}
                  exact={true}
                ></UserRoute>
                <UserRoute
                  path="/settings"
                  component={PunterSettings}
                  exact={true}
                ></UserRoute>

                <UserRoute
                  path="/profile"
                  component={MyProfile}
                  exact={true}
                ></UserRoute>
                <Route exact path="/" render={() => <Redirect to="/home" />} />
                <Route path="/rules" render={() => <RulesAndRegulations />} />
                <Route
                  path="/responsible-gambling"
                  render={() => <ResponsibleGambling />}
                />
                <Route
                  path="/responsible-gaming"
                  render={() => (
                    <ResponsibleGaming
                      name="Responsible Gaming"
                      eventTypeID="Responsible Gaming"
                    />
                  )}
                />
                <Route
                  path="/about-us"
                  render={() => (
                    <ResponsibleGaming name="About Us" eventTypeID="About Us" />
                  )}
                />
                <Route
                  path="/terms-conditions"
                  render={() => (
                    <ResponsibleGaming
                      name="Terms and conditions"
                      eventTypeID="Terms and conditions"
                    />
                  )}
                />
                <Route
                  path="/set-button-variables"
                  render={() => <ButtonVariables />}
                />
                <Route path="/promotion" component={Promotion} exact={true} />
                <Route path="/sitemap" component={Sitemap} exact={true} />
                <>
                  <UserRoute
                    path="/transaction/withdraw"
                    component={Withdrawal}
                    exact={true}
                  ></UserRoute>
                  <UserRoute
                    path="/transaction/deposit"
                    component={Deposit}
                    exact={true}
                  ></UserRoute>
                  <UserRoute
                    path="/my_transactions"
                    component={TransactionRequest}
                    exact={true}
                  ></UserRoute>
                </>
              </Switch>
            </div>

            <Dialog
              open={showNotificationModal}
              onClose={closeStlDialog}
              aria-labelledby="Settlements Dialog"
              fullScreen={false}
              fullWidth={true}
              maxWidth="md"
              className="stl-dialog"
            >
              <DialogTitle className="stl-dialog-title">
                <div className="modal-title notification-title">
                  <NotificationsIcon />
                  Notification
                </div>
                <IconButton
                  className="close-btn"
                  onClick={() => setShowNotificationModal(false)}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent className="stl-dialog-content">
                <AdminNotification />
              </DialogContent>
            </Dialog>
          </IonContent>
          <div className="mob-header">
            <MobileHeader />
          </div>
          {isMobile || isIOS ? (
            <div className="mob-view">
              <MlobbyIframeNew />
            </div>
          ) : null}
        </IonApp>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  if (!state.auth.loggedIn) {
    return {
      role: null,
      status: -1,
      allowedConfig: state.common.allowedConfig,
    };
  }
  let claim = state.auth?.jwtToken?.split('.')?.[1] || '';
  const role = JSON.parse(window.atob(claim)).role;
  const status = JSON.parse(window.atob(claim)).sts;
  return {
    loggedIn: state.auth.loggedIn,
    role: role,
    status,
    allowedConfig: state.common.allowedConfig,
    commissionEnabled: state.common.commissionEnabled,
    balanceChanged: state.common.balanceChanged,
    notificationUpdated: state.common.notificationUpdated,
    houseId: getHouseIdFromToken(),
    parentId: getParentIdFromToken(),
    accountId: sessionStorage.getItem('aid'),
    pushNotifWSConnection: state.exchangeSports.pushNotifWSConnection,
  };
};

const mapDispatchToProps = (dispatch: Function) => {
  return {
    fetchBalance: () => dispatch(fetchBalance()),
    setAllowedConfig: (allowedConfig: number) =>
      dispatch(setAllowedConfig(allowedConfig)),
    setDomainConfig: (config: DomainConfig) =>
      dispatch(setDomainConfig(config)),
    setEnableCommission: (commission: boolean) =>
      dispatch(enableCommission(commission)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserRouter);
